/**
 * Authentication error codes and types shared between backend and frontend.
 *
 * These error codes are used by:
 * - Backend (Convex): To throw structured authentication errors via ConvexError
 * - Frontend (Web App): To detect and handle auth errors gracefully
 *
 * @example
 * ```typescript
 * // Backend usage
 * import { AUTH_ERROR_CODES, throwAuthError } from "@repo/shared/auth-errors";
 * throwAuthError(AUTH_ERROR_CODES.NOT_AUTHENTICATED, "Not authenticated");
 *
 * // Frontend usage
 * import { AUTH_ERROR_CODES, isAuthErrorCode } from "@repo/shared/auth-errors";
 * if (isAuthErrorCode(data.code)) { ... }
 * ```
 *
 * @module
 */
/**
 * Error codes for authentication failures.
 * Used by frontend to distinguish error types and handle appropriately.
 */
export const AUTH_ERROR_CODES = {
    /** User is not authenticated (no valid JWT) */
    NOT_AUTHENTICATED: "NOT_AUTHENTICATED",
    /** Authentication is missing required email claim */
    INVALID_AUTH: "INVALID_AUTH",
    /** User record not found in database */
    USER_NOT_FOUND: "USER_NOT_FOUND",
    /** User is not a member of the requested organization */
    NOT_ORG_MEMBER: "NOT_ORG_MEMBER",
};
/**
 * Type guard to check if a value is a valid auth error code.
 */
export function isAuthErrorCode(value) {
    return (typeof value === "string" && Object.values(AUTH_ERROR_CODES).includes(value));
}
/**
 * Type guard to check if an object matches the AuthErrorData shape.
 * Used for runtime validation of error payloads.
 */
export function isAuthErrorData(data) {
    if (!data || typeof data !== "object")
        return false;
    const obj = data;
    return ("code" in obj &&
        "message" in obj &&
        isAuthErrorCode(obj.code) &&
        typeof obj.message === "string");
}
